'use client';

import { ChevronDown, ChevronRight, Crop, ImagePlus, Plus, SlidersHorizontal, Video } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { UserMaterial } from '@/lib/api/materials';

const focusClass = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9] focus-visible:ring-offset-2';

/** Video/image inputs stay visible on phones; detailed parameters use a disclosure. */
export function KagemaneMobileSetup({
  referenceName, referencePoster, hasReference, materials, optionalPeople, modelLabel, resolution,
  expanded, disabled, validationMessage, settingsHint, onToggleSettings, onChangeReference, onAddMaterials, onEditReference,
}: {
  referenceName: string; referencePoster?: string; hasReference: boolean;
  materials: UserMaterial[]; optionalPeople: boolean; modelLabel: string; resolution: string;
  expanded: boolean; disabled: boolean;
  validationMessage?: string; settingsHint?: string;
  onToggleSettings: () => void; onChangeReference: () => void; onAddMaterials: () => void;
  onEditReference?: () => void;
}) {
  const t = useTranslations('kagemane');
  const editT = useTranslations('referenceVideoEditor');
  const material = materials[0];
  const materialPoster = material?.thumbnail_url || material?.url;
  return <div className="w-full min-w-0 p-3 lg:hidden" data-kagemane-mobile-setup>
    <div className="mb-3 flex items-center justify-between gap-2">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold leading-5">{t('mobileSetupTitle')}</h2>
        <p className="mt-0.5 truncate text-xs font-normal leading-5 text-[#667680]" title={`${modelLabel} · ${resolution}`}>{modelLabel} · {resolution}</p>
      </div>
      <button type="button" aria-expanded={expanded} aria-controls="kagemane-setup-fields"
        aria-describedby={!expanded && (validationMessage || settingsHint) ? 'kagemane-mobile-setup-status' : undefined} onClick={onToggleSettings}
        className={`inline-grid h-11 shrink-0 grid-cols-[14px_auto_14px] items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-[#DCE5EB] bg-white px-3 text-xs font-medium leading-[14px] text-[#40545F] hover:bg-[#F4F7F9] ${focusClass}`}>
        <SlidersHorizontal className="block size-3.5" aria-hidden />
        <span className="block leading-[14px]">{t(expanded ? 'mobileHideSettings' : 'mobileSettings')}</span>
        <ChevronDown className={`block size-3.5 transition-transform motion-reduce:transition-none ${expanded ? 'rotate-180' : ''}`} aria-hidden />
      </button>
    </div>
    <div className="overflow-hidden rounded-xl border border-[#E3E9ED] bg-[#F8FAFB]">
      <div className="flex min-w-0 items-center border-b border-[#E3E9ED]">
        <button type="button" disabled={disabled} onClick={onChangeReference}
          aria-label={hasReference ? t('changeReferenceAria', { name: referenceName }) : t('referenceUploadPlaceholder')}
          className={`flex min-h-[68px] min-w-0 flex-1 items-center gap-3 rounded-t-xl px-3 py-2 text-left hover:bg-[#EDF6FA] disabled:opacity-50 ${focusClass}`}>
          <span className="grid h-11 w-9 shrink-0 place-items-center overflow-hidden rounded-lg border border-[#DCE5EB] bg-white text-[#74848D]">
            {hasReference && referencePoster ? <img src={referencePoster} alt="" className="h-full w-full object-cover" /> : <Video className="size-4" aria-hidden />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[11px] font-medium leading-4 text-[#667680]">{t('referenceVideo')}</span>
            <span className="mt-0.5 block truncate text-sm font-medium leading-5">{hasReference ? referenceName : t('noReference')}</span>
          </span>
          <ChevronRight className="size-4 shrink-0 text-[#74848D]" aria-hidden />
        </button>
        {onEditReference && <button type="button" disabled={disabled} onClick={onEditReference} aria-label={editT('title')} title={editT('title')}
          className={`mr-1 grid size-11 shrink-0 place-items-center rounded-lg text-[#40545F] hover:bg-[#EDF6FA] disabled:opacity-50 ${focusClass}`}><Crop className="size-4" aria-hidden /></button>}
      </div>
      <button type="button" disabled={disabled} onClick={onAddMaterials} aria-label={t(optionalPeople ? 'genjutsuOptionalPeople' : 'materialDropTitle')}
        className={`flex min-h-[68px] w-full min-w-0 items-center gap-3 rounded-b-xl px-3 py-2 text-left hover:bg-[#EDF6FA] disabled:opacity-50 ${focusClass}`}>
        <span className="grid h-11 w-9 shrink-0 place-items-center overflow-hidden rounded-lg border border-dashed border-[#BDD1DC] bg-white text-[#176B89]">
          {materialPoster ? <img src={materialPoster} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="size-4" aria-hidden />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium leading-5">{t(optionalPeople ? 'genjutsuOptionalPeople' : 'materials')}</span>
          <span className="mt-0.5 block text-xs font-normal leading-4 text-[#667680]" aria-live="polite">{materials.length ? t('mobileImagesSelected', { count: materials.length }) : t(optionalPeople ? 'materialDropHint' : 'requiredMaterials')}</span>
        </span>
        <Plus className="size-4 shrink-0 text-[#176B89]" aria-hidden />
      </button>
    </div>
    {!expanded && (validationMessage || settingsHint) && <p id="kagemane-mobile-setup-status" role={validationMessage ? 'alert' : 'status'}
      className={`mt-2 text-xs font-normal leading-5 ${validationMessage ? 'text-red-600' : 'text-[#667680]'}`}>{validationMessage || settingsHint}</p>}
  </div>;
}
